import React, { createContext, useState, Dispatch, SetStateAction, useEffect } from 'react';
import { useGetUserCredit } from '../hooks/user/useGetUserCredit';
import { useAppContext } from '../hooks';
import { useAiModelDetails } from '../hooks/admin/useAiModelDetails';
import { AiVideoModelEnum } from '../utils';
import { AiVideoModelDetails } from '../@types';

interface CreditContextType {
  usableCredits: number;
  setUsableCredits: Dispatch<SetStateAction<number>>;
  handleUpdateUserCredit: () => void;
  isFetchingCredit: boolean;
  totalCredits: number;
  modelDetails: Record<AiVideoModelEnum, AiVideoModelDetails>;
  isUserSubscribed: boolean;
  getModelData: (val: string) => any;
  userPlanData: string;
  agentPlanData: string;
  hadPreviousActiveSubscription: boolean;
}
export const CreditContext = createContext<CreditContextType | null>(null);

export function CreditProvider({ children }) {
  const [usableCredits, setUsableCredits] = useState(null);
  const [totalCredits, setTotalCredits] = useState(null);
  const [isUserSubscribed, setIsUserSubscribed] = useState(false);
  const [userPlanData, setUserPlanData] = useState<
    'activePlan' | 'freeTrialAccess' | 'noActivePlan'
  >('activePlan');
  const [agentPlanData, setAgentPlanData] = useState<
    'agentPlan' | 'agentTrialPlan' | 'noAgentPlan'
  >('agentPlan');
  const [hadPreviousActiveSubscription, setHadPreviousActiveSubscription] = useState(false);

  const { mutate: mutateGetUserCredit, isLoading: isFetchingCredit } = useGetUserCredit();
  const { userData, isUserDataLoading } = useAppContext();
  const { models: modelDetails, loading, getModelData } = useAiModelDetails();

  useEffect(() => {
    if (!isUserSubscribed && totalCredits > 0) {
      setUserPlanData('freeTrialAccess');
    } else if (!isUserSubscribed && totalCredits === 0) {
      setUserPlanData('noActivePlan');
    } else if (isUserSubscribed) {
      setUserPlanData('activePlan');
    }
  }, [isUserSubscribed, totalCredits]);

  const handleUpdateUserCredit = () => {
    mutateGetUserCredit(undefined, {
      onSuccess: (data) => {
        const availableCredit = data?.availableCredits - data?.creditsInUse;
        const totalCredits = data?.totalCredits;
        setTotalCredits(totalCredits);
        setUsableCredits(availableCredit > 0 ? availableCredit : 0);
        setIsUserSubscribed(data?.isUserSubscribed);
        if (data?.hasAgentTrialPlan) {
          setAgentPlanData('agentTrialPlan');
        } else if (data?.hasAgentPlan) {
          setAgentPlanData('agentPlan');
        } else if (!data?.hasAgentPlan && !data?.hasAgentTrialPlan) {
          setAgentPlanData('noAgentPlan');
        }
        setHadPreviousActiveSubscription(data?.hadPreviousActiveSubscription);
      },
      onError: () => {
        // TODO:
      },
    });
  };
  useEffect(() => {
    if (userData) handleUpdateUserCredit();
  }, [userData]);

  return (
    <CreditContext.Provider
      value={{
        usableCredits,
        setUsableCredits,
        handleUpdateUserCredit,
        modelDetails,
        isFetchingCredit,
        totalCredits,
        isUserSubscribed,
        getModelData,
        userPlanData,
        agentPlanData,
        hadPreviousActiveSubscription,
      }}
    >
      {children}
    </CreditContext.Provider>
  );
}
